import axiosInstance from "../../components/axiosApi";
import { toast } from "react-toastify";
import { triggerNativeDownload } from "./triggerNativeDownload";

/**
 * Request a signed download URL for `filename` and trigger the native browser
 * download. The canonical entry point for every download in the Data Download
 * section so the toast UX and error handling stay consistent.
 *
 * Flow:
 * 1. toast.info("X — preparing download") — instant feedback while the POST
 *    is in flight
 * 2. POST /api/files/download-url/ with the JWT to get a signed URL + size
 * 3. triggerNativeDownload — opens the file in the browser's native download
 *    manager (which is where the user sees real progress / cancel)
 *
 * On failure: surfaces the backend's `detail` field in the toast so users see
 * WHY a download failed (e.g. "No file with name X"), not just THAT it failed.
 * Falls back to "could not start download" if the error has no detail.
 *
 * @param {Object}  params
 * @param {string}  params.filename  On-disk filename the backend resolves
 *                                   under DOWNLOAD_BASE_DIR (e.g. "Hexagonal_Data.7z").
 *                                   Also the filename hint passed to the browser.
 * @param {string} [params.label]    Human-readable label for the toast (e.g.
 *                                   "Hexagonal Data"). Defaults to `filename`.
 *                                   For dynamic-filename triggers (per-row file
 *                                   search), pass the wrapped filename so the
 *                                   toast reads naturally.
 */
export async function requestSignedDownload({ filename, label }) {
  const displayLabel = label || filename;
  toast.info(`${displayLabel} — preparing download`);
  try {
    const { data } = await axiosInstance.post("/api/files/download-url/", {
      file_name: filename,
    });
    triggerNativeDownload(data.url, filename, data.size_bytes);
  } catch (err) {
    const detail = err?.response?.data?.detail || "could not start download";
    toast.error(`${displayLabel} — ${detail}`);
  }
}
